import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Button } from "primereact/button";
import { useAuth } from "../../common/contexts/AuthContext";

// Shown when a trial has ended (or the subscription lapsed) and as the target of
// the trial banner's "Upgrade". Lists paid plans; checkout goes through Stripe.
// If online payment isn't set up yet, it falls back to "contact us".
const UpgradePage = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth() || {};
  const [status, setStatus] = useState(null);
  const [plans, setPlans] = useState([]);
  const [cycle, setCycle] = useState("monthly");
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    axios.get("/api/billing/status").then((r) => setStatus(r.data?.data || null)).catch(() => {});
    axios.get("/api/plans")
      .then((r) => {
        const list = Array.isArray(r.data) ? r.data : r.data?.data || [];
        setPlans(list.filter((p) => p.tier !== "free" && p.isActive !== false));
      })
      .catch(() => {});
  }, []);

  const schoolId = user?.schoolId || localStorage.getItem("schoolId");
  const blocked = status?.blocked;

  const checkout = async (plan) => {
    setBusy(plan.id); setError("");
    try {
      const r = await axios.post("/api/billing/checkout", { schoolId, planId: plan.id, billingCycle: cycle });
      if (r.data?.url) { window.location.href = r.data.url; return; }
      throw new Error(r.data?.message || "Checkout unavailable");
    } catch (e) {
      setError("Online payment isn't available right now. Email contact@devzonesolutions.com and we'll upgrade your school straight away.");
      setBusy(null);
    }
  };

  const price = (p) => {
    const cents = cycle === "annual" ? p.annualPrice : p.monthlyPrice;
    return cents ? `$${(cents / 100).toFixed(0)}/${cycle === "annual" ? "year" : "month"}` : "Contact us";
  };

  return (
    <div style={{ maxWidth: 960, margin: "0 auto", padding: "48px 20px" }}>
      <h1 style={{ marginTop: 0 }}>
        {blocked === "trial_expired" ? "Your free trial has ended"
          : blocked ? "Your subscription is not active"
          : "Upgrade Schoolwise"}
      </h1>
      <p style={{ fontSize: 16, color: "var(--text-color-secondary)", maxWidth: 640 }}>
        {blocked
          ? "Access is paused, but your school's data is safe. Choose a plan to pick up exactly where you left off."
          : status?.daysLeft != null
          ? `You have ${status.daysLeft} day${status.daysLeft === 1 ? "" : "s"} left in your trial. Upgrade any time — nothing changes for your staff or parents.`
          : "Choose the plan that fits your school."}
      </p>

      <div style={{ display: "flex", gap: 8, margin: "20px 0" }}>
        {["monthly", "annual"].map((c) => (
          <Button key={c} label={c === "monthly" ? "Monthly" : "Annual (save ~17%)"}
            className={cycle === c ? "" : "p-button-outlined"} onClick={() => setCycle(c)} />
        ))}
      </div>

      {error && <div style={{ background: "#fff4e5", color: "#8a4b00", padding: 12, borderRadius: 8, marginBottom: 16 }}>{error}</div>}

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 16 }}>
        {plans.map((p) => (
          <div key={p.id} style={{ border: "1px solid var(--surface-border, #ddd)", borderRadius: 12, padding: 20 }}>
            <h3 style={{ marginTop: 0 }}>{p.name}{status?.plan?.id === p.id ? " · current trial" : ""}</h3>
            <div style={{ fontSize: 24, fontWeight: 700, marginBottom: 8 }}>{price(p)}</div>
            <div style={{ fontSize: 14, color: "var(--text-color-secondary)", marginBottom: 16 }}>
              {p.maxStudents ? `Up to ${p.maxStudents} students` : "Unlimited students"}
              {p.maxStaff ? ` · ${p.maxStaff} staff` : ""}
            </div>
            <Button label={busy === p.id ? "Opening checkout…" : "Choose " + p.name}
              disabled={!!busy || !schoolId} onClick={() => checkout(p)} style={{ width: "100%" }} />
          </div>
        ))}
      </div>

      <div style={{ marginTop: 28, display: "flex", gap: 12, flexWrap: "wrap" }}>
        <a href="mailto:contact@devzonesolutions.com?subject=Upgrade%20Schoolwise">Talk to us about pricing</a>
        {!blocked && <Button label="Back to dashboard" className="p-button-text" onClick={() => navigate("/index/dashboard")} />}
        {blocked && logout && <Button label="Log out" className="p-button-text" onClick={logout} />}
      </div>
    </div>
  );
};

export default UpgradePage;
